import React from 'react';
import { Link, makeStyles } from '@material-ui/core';
import AccessibleIcon from '@material-ui/icons/Accessible';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import Typography from 'components/Typography';
import {
  IconType,
  JobSheetData,
  JobSheetDataRowsOptions
} from 'models/JobDetails';
import { SummaryPropertyType } from 'views/Jobs/AddJobModal/SummaryProperty';

export const TAB_COLUMN_WIDTH_UNITS = 70;

const useStyles = makeStyles(theme => ({
  panel: {
    display: 'flex',
    gap: theme.spacing(4)
  },
  tabColumn: {
    width: theme.spacing(TAB_COLUMN_WIDTH_UNITS),
    padding: theme.spacing(0, 2)
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.aegis.semantic.background.canvas}`
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.aegis.semantic.background.canvas}`
  },
  label: {
    flex: '1 0 0',
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    color: theme.palette.aegis.semantic.message.black
  },
  labelIcon: {
    display: 'flex',
    '& .MuiSvgIcon-root': {
      fontSize: theme.spacing(3)
    },
    '& > .info': {
      color: theme.palette.aegis.semantic.message.dark
    },
    '& > .error': {
      color: theme.palette.aegis.colour.grey.dark
    },
    '& > .vulnerable': {
      color: theme.palette.aegis.semantic.warning.base
    }
  },
  value: {
    color: theme.palette.aegis.semantic.message.dark,
    textAlign: 'right'
  },
  valueOnClick: {
    color: theme.palette.aegis.semantic.message.dark,
    textAlign: 'right',
    cursor: 'pointer',
    textDecoration: 'underline'
  },
  multilineValue: {
    color: theme.palette.aegis.semantic.message.dark
  },
  multilineValueOnClick: {
    width: 'fit-content',
    color: theme.palette.aegis.semantic.message.dark,
    cursor: 'pointer',
    textDecoration: 'underline'
  },
  link: {
    color: theme.palette.aegis.semantic.message.dark
  },
  sectionTitle: {
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${theme.palette.aegis.semantic.background.canvas}`
  }
}));

const Icon = ({ type }: { type: IconType }) => {
  if (type === IconType.VULNERABLE) {
    return <AccessibleIcon className="vulnerable" />;
  }
  if (type === IconType.INFO) {
    return <InfoIcon className="info" />;
  }
  if (type === IconType.ERROR) {
    return <ErrorIcon className="error" />;
  }
  return null;
};

const RowLabel = ({ row }: { row: JobSheetDataRowsOptions }) => {
  const classes = useStyles();

  return (
    <Typography className={classes.label} data-testid="rowLabel">
      {row.label}
      <span className={classes.labelIcon}>
        {row.icon !== undefined && <Icon type={row.icon} />}
      </span>
    </Typography>
  );
};

const SingleValue = ({ row }: { row: JobSheetDataRowsOptions }) => {
  const classes = useStyles();

  if (row.onClick) {
    return (
      <Typography
        className={classes.valueOnClick}
        onClick={row.onClick}
        data-testid="rowValueOnClick"
      >
        {row.value}
      </Typography>
    );
  }

  if (row.type === SummaryPropertyType.LINK) {
    return (
      <Typography className={classes.value} data-testid="rowValue">
        <Link
          href={row.value as string}
          className={classes.link}
          target="_blank"
        >
          {row.value}
        </Link>
      </Typography>
    );
  }

  return (
    <Typography className={classes.value} data-testid="rowValue">
      {row.value}
    </Typography>
  );
};

const Row = ({ row }: { row: JobSheetDataRowsOptions }) => {
  const classes = useStyles();

  if (row.type === 'textarea') {
    const lines = Array.isArray(row.value) ? row.value : [row.value];

    return (
      <div className={classes.column}>
        <RowLabel row={row} />
        <div>
          {lines.map((line, index) =>
            row.onClick && index === 0 ? (
              <Typography
                key={line}
                className={classes.multilineValueOnClick}
                onClick={row.onClick}
                data-testid="rowValueMultilineOnClick"
              >
                {line}
              </Typography>
            ) : (
              <Typography
                key={line}
                className={classes.multilineValue}
                data-testid="rowValueMultiline"
              >
                {line}
              </Typography>
            )
          )}
        </div>
      </div>
    );
  }

  if (row.type === 'list' || row.icon !== undefined) {
    const [firstLine, ...otherLines] = Array.isArray(row.value)
      ? row.value
      : [row.value];

    return (
      <>
        <div className={classes.column}>
          <RowLabel row={row} />
          <Typography
            className={classes.multilineValue}
            data-testid="rowValueMultiline"
          >
            {firstLine}
          </Typography>
        </div>
        {otherLines.map(line => (
          <div key={line} className={classes.row}>
            <Typography
              className={classes.multilineValue}
              data-testid="rowValueMultiline"
            >
              {line}
            </Typography>
          </div>
        ))}
      </>
    );
  }

  return (
    <div className={classes.row}>
      <RowLabel row={row} />
      <SingleValue row={row} />
    </div>
  );
};

interface NewJobSheetPanelProps {
  rows: JobSheetDataRowsOptions[];
  sections?: JobSheetData[];
}

export const NewJobSheetPanel = ({ rows, sections }: NewJobSheetPanelProps) => {
  const classes = useStyles();

  return (
    <div className={classes.panel}>
      <div className={classes.tabColumn} data-testid="tabColumn">
        {rows.map(row => (
          <Row key={`${row.label}${row.value}`} row={row} />
        ))}
      </div>
      {sections && sections.length > 0 && (
        <div className={classes.tabColumn} data-testid="tabColumn">
          {sections.map(section => (
            <React.Fragment key={section.label}>
              <Typography
                variant="h2"
                className={classes.sectionTitle}
                data-testid="sectionTitle"
              >
                {section.label}
              </Typography>
              {section.rows.map(row => (
                <Row key={`${row.label}${row.value}`} row={row} />
              ))}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};
